import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/lib/useReducedMotion';
import { onSectionChange, onZap, type ZapPoint } from './zapEvents';

type Side = -1 | 1;
type Route = 'right' | 'left' | 'zigzag';
type Curve = [ZapPoint, ZapPoint, ZapPoint, ZapPoint];

interface Chain { start: number; step: number; bulge: number; hue: string }
interface Card { el: HTMLElement; rect: DOMRect; color: string; charge: number; writtenCharge: number }
interface Chip { x: number; y: number; hue: string }
interface Stream {
  kind: 'stream'; stack: number; chain: Chain; route: Route; from: number; to: number;
  fromSide: Side; toSide: Side; drop: boolean; path: Curve; started: number;
  duration: number; trail: ZapPoint[]; hops: number;
}
interface Bolt { kind: 'bolt'; path: Curve; started: number; duration: number; trail: ZapPoint[]; aim: number; stack: number; hue: string }
interface Flyer { kind: 'flyer'; path: Curve; started: number; duration: number; trail: ZapPoint[]; hue: string }
type Actor = Stream | Bolt | Flyer;
interface Ring { x: number; y: number; hue: string; started: number; duration: number; radius: number }
interface Burst { x: number; y: number; hue: string; started: number; duration: number }

const CHAINS: Chain[] = [
  { start: 0, step: 2, bulge: 30, hue: '#E4705E' },
  { start: 0, step: 3, bulge: 56, hue: '#7C9CFF' },
  { start: 1, step: 3, bulge: 40, hue: '#3FBFA0' },
  { start: 1, step: 2, bulge: 22, hue: '#D9A441' },
];
const ROUTES: Route[] = ['right', 'left', 'zigzag'];
const MAX_STREAMS = 3;
const MAX_BOLTS = 1;
const MAX_FLYERS = 2;
const MAX_MOBILE_STREAMS_PER_STACK = 2;
const MAX_RINGS = 10;
const MAX_BURSTS = 8;
const MAX_TRAIL_POINTS = 14;
const SCROLL_SETTLE_MS = 160;
const LAYOUT_SETTLE_MS = 180;

const random = (max: number) => Math.floor(Math.random() * max);
const rgba = (hex: string, alpha: number) => {
  const value = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},${alpha})`;
};
const cubic = (path: Curve, t: number): ZapPoint => {
  const u = 1 - t, a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
  return {
    x: a * path[0].x + b * path[1].x + c * path[2].x + d * path[3].x,
    y: a * path[0].y + b * path[1].y + c * path[2].y + d * path[3].y,
  };
};
const durationFor = (path: Curve) => {
  const straight = Math.hypot(path[3].x - path[0].x, path[3].y - path[0].y);
  const curve = Math.hypot(path[1].x - path[0].x, path[1].y - path[0].y) + Math.hypot(path[3].x - path[2].x, path[3].y - path[2].y);
  return Math.max(440, Math.min(1500, (straight + curve * 0.42) / 0.72));
};

/** Recreates the original curved zaps while bounding work to visible cards and live actors. */
export const ZapEffects = () => {
  const reduced = useReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasElement: HTMLCanvasElement = canvas;

    // Reduced motion keeps the scroll hover guard, but never creates a canvas loop.
    if (reduced) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const onScroll = () => {
        canvasElement.dataset.scrolling = 'true';
        clearTimeout(timer);
        timer = setTimeout(() => { canvasElement.dataset.scrolling = 'false'; }, SCROLL_SETTLE_MS);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        clearTimeout(timer);
        delete canvasElement.dataset.scrolling;
        window.removeEventListener('scroll', onScroll);
      };
    }

    const ctx = canvasElement.getContext('2d');
    if (!ctx) return;

    let width = 0, height = 0, dpr = 1, raf = 0, lastPaint = 0;
    let disposed = false, scrolling = false, suspended = false, resizePending = false, painting = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    let scrollTimer: ReturnType<typeof setTimeout> | undefined;
    let ambientTimer: ReturnType<typeof setTimeout> | undefined;
    let nextStreamAt = 0, nextBoltAt = 0, stackCursor = 0, boltCursor = 0;
    let measures = 0, frames = 0;
    let cachedActorCount = -1, cachedStreamCount = -1;
    const actors: Actor[] = [];
    const rings: Ring[] = [];
    const bursts: Burst[] = [];
    const stacks: Card[][] = [];
    const chips: Chip[] = [];
    const knownCards = new Set<HTMLElement>();
    const watchedColumns = new Set<Element>();
    const main = document.querySelector<HTMLElement>('#main-content');
    const activeSource: { point: ZapPoint | null } = { point: null };
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const clearCanvas = () => ctx.clearRect(0, 0, width, height);
    const stopFrame = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };
    const resetCharges = () => {
      for (const el of knownCards) el.style.setProperty('--rd-charge', '0');
      for (const stack of stacks) for (const card of stack) {
        card.charge = 0;
        card.writtenCharge = 0;
      }
    };
    const resetActors = () => {
      actors.length = 0;
      rings.length = 0;
      bursts.length = 0;
      updateActorCounts();
      resetCharges();
      clearCanvas();
    };
    const updateActorCounts = () => {
      const actorCount = actors.length;
      const streamCount = actors.reduce((count, actor) => count + Number(actor.kind === 'stream'), 0);
      if (actorCount !== cachedActorCount) {
        canvasElement.dataset.activeActors = String(actorCount);
        cachedActorCount = actorCount;
      }
      if (streamCount !== cachedStreamCount) {
        canvasElement.dataset.activeStreams = String(streamCount);
        cachedStreamCount = streamCount;
      }
    };
    const hideCharge = (card: Card) => {
      card.charge = 0;
      card.writtenCharge = 0;
      card.el.style.setProperty('--rd-charge', '0');
    };
    const lightCard = (card: Card | undefined) => {
      if (!card) return;
      card.charge = 1;
      card.writtenCharge = 1;
      card.el.style.setProperty('--rd-charge', '1');
    };
    const addImpact = (point: ZapPoint, hue: string, time: number, scale = 1) => {
      if (rings.length < MAX_RINGS) rings.push({ x: point.x, y: point.y, hue, started: time, duration: 620, radius: 36 * scale });
      if (bursts.length < MAX_BURSTS) bursts.push({ x: point.x, y: point.y, hue, started: time, duration: 220 });
    };
    const resetTimers = () => {
      clearTimeout(ambientTimer);
      clearTimeout(settleTimer);
      clearTimeout(scrollTimer);
      ambientTimer = undefined;
      settleTimer = undefined;
      scrollTimer = undefined;
    };

    const isOnScreen = (rect: DOMRect) => rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < height && rect.right > 0 && rect.left < width;
    const anchor = (card: Card, side: Side): ZapPoint => ({
      x: side === 1 ? card.rect.right + 1 : card.rect.left - 1,
      y: card.rect.top + card.rect.height / 2,
    });
    const pathForStream = (from: ZapPoint, to: ZapPoint, fromSide: Side, toSide: Side, bulge: number, drop: boolean): Curve => {
      const dy = to.y - from.y;
      if (drop) {
        return [from, { x: from.x, y: from.y + dy * 0.45 }, { x: to.x + toSide * bulge, y: to.y - Math.min(44, Math.abs(dy) * 0.15) }, to];
      }
      return [from, { x: from.x + fromSide * bulge, y: from.y + dy * 0.30 }, { x: to.x + toSide * bulge, y: to.y - dy * 0.30 }, to];
    };
    const queueLayoutRefresh = (delay = LAYOUT_SETTLE_MS) => {
      if (disposed || reduced) return;
      if (!suspended) {
        suspended = true;
        stopFrame();
        resetActors();
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        settleTimer = undefined;
        if (disposed || scrolling || document.hidden) return;
        if (resizePending) applySize();
        measure();
        suspended = false;
        nextStreamAt = performance.now() + 450;
        nextBoltAt = performance.now() + 1800;
        scheduleAmbient();
      }, delay);
    };
    const onResize = () => {
      resizePending = true;
      queueLayoutRefresh();
    };
    const applySize = () => {
      resizePending = false;
      width = Math.max(1, window.innerWidth);
      height = Math.max(1, window.innerHeight);
      const phone = width < 640;
      dpr = Math.min(window.devicePixelRatio || 1, phone ? 1 : 1.5);
      canvasElement.width = Math.round(width * dpr);
      canvasElement.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      clearCanvas();
    };
    function measure() {
      const prevCards = new Set(knownCards);
      stacks.length = 0;
      chips.length = 0;
      activeSource.point = null;

      const panel = document.querySelector<HTMLElement>('[role="tabpanel"]:not([hidden])');
      const columns = panel ? [...panel.querySelectorAll<HTMLElement>('[data-rd-cards]')] : [];
      const visibleColumns = new Set<Element>(columns);
      for (const column of watchedColumns) {
        if (!visibleColumns.has(column)) {
          resizeObserver?.unobserve(column);
          watchedColumns.delete(column);
        }
      }
      for (const column of columns) {
        if (!watchedColumns.has(column)) {
          resizeObserver?.observe(column);
          watchedColumns.add(column);
        }
        const elements = [...column.querySelectorAll<HTMLElement>(':scope > [data-rd-card]')];
        const cards: Card[] = [];
        for (const el of elements) {
          prevCards.delete(el);
          knownCards.add(el);
          const rect = el.getBoundingClientRect();
          if (!isOnScreen(rect)) continue;
          const color = /^#[0-9a-f]{6}$/i.test(el.dataset.rdColor ?? '') ? el.dataset.rdColor! : '#8fe3f0';
          const charge = Number.parseFloat(el.style.getPropertyValue('--rd-charge')) || 0;
          cards.push({ el, rect, color, charge, writtenCharge: charge });
        }
        if (cards.length) stacks.push(cards);
      }
      for (const el of prevCards) {
        el.style.setProperty('--rd-charge', '0');
        knownCards.delete(el);
      }

      const source = [...document.querySelectorAll<HTMLElement>('[data-rd-source]')]
        .map(el => ({ el, rect: el.getBoundingClientRect() }))
        .find(({ el, rect }) => isOnScreen(rect) && getComputedStyle(el).visibility !== 'hidden' && Number.parseFloat(getComputedStyle(el).opacity || '1') > 0.1);
      if (source) activeSource.point = { x: source.rect.left + source.rect.width / 2, y: source.rect.top + source.rect.height / 2 };

      const tabs = [...document.querySelectorAll<HTMLElement>('[data-rd-tab]')];
      for (const tab of tabs) {
        const rect = tab.getBoundingClientRect();
        if (!isOnScreen(rect) || getComputedStyle(tab).visibility === 'hidden') continue;
        const hue = /^#[0-9a-f]{6}$/i.test(tab.dataset.rdHue ?? '') ? tab.dataset.rdHue! : '#7C9CFF';
        chips.push({ x: rect.left + rect.width / 2, y: rect.bottom - 1, hue });
      }
      measures++;
      canvasElement.dataset.measures = String(measures);
    }

    const chooseChain = (cards: Card[], active: Stream[]) => {
      const valid = CHAINS.filter(chain => chain.start + chain.step < cards.length);
      if (valid.length && width >= 640) return valid[random(valid.length)];
      if (valid.length && width < 640) {
        // Spread parallel mobile paths through the column instead of layering both at its top.
        const candidates = valid.flatMap(chain => Array.from(
          { length: cards.length - chain.step },
          (_, start) => ({ ...chain, start }),
        ));
        const separated = candidates.filter(candidate => active.every(stream =>
          Math.abs(candidate.start - stream.from) > 1 && Math.abs(candidate.start - stream.to) > 1,
        ));
        const pool = separated.length ? separated : candidates;
        if (pool.length) return pool[random(pool.length)];
      }
      return { start: 0, step: 1, bulge: 34, hue: '#8FE3F0' };
    };
    const routeSides = (route: Route): [Side, Side] => {
      const first: Side = route === 'right' ? 1 : route === 'left' ? -1 : Math.random() < 0.5 ? 1 : -1;
      return [first, route === 'zigzag' ? (first === 1 ? -1 : 1) : first];
    };
    const pushActor = (actor: Actor) => {
      actors.push(actor);
      updateActorCounts();
      if (!painting && !raf && !suspended && !scrolling && !document.hidden) raf = requestAnimationFrame(frame);
    };
    const spawnStream = (stackIndex: number, now: number, dropFrom?: ZapPoint) => {
      const streams = actors.filter((actor): actor is Stream => actor.kind === 'stream');
      if (streams.length >= MAX_STREAMS) return false;
      const stackStreams = streams.filter(actor => actor.stack === stackIndex);
      const stackLimit = width < 640 ? MAX_MOBILE_STREAMS_PER_STACK : 1;
      if (stackStreams.length >= stackLimit) return false;
      const cards = stacks[stackIndex];
      if (!cards || cards.length < (dropFrom ? 1 : 2)) return false;
      const chain = chooseChain(cards, stackStreams);
      const start = dropFrom ? 0 : chain.start;
      const to = dropFrom ? 0 : Math.min(cards.length - 1, start + chain.step);
      if (to === start && !dropFrom) return false;
      const availableRoutes = ROUTES.filter(route => !stackStreams.some(stream => stream.route === route));
      const routes = availableRoutes.length ? availableRoutes : ROUTES;
      const route = routes[random(routes.length)];
      const [fromSide, toSide] = routeSides(route);
      const from = dropFrom ?? anchor(cards[start], fromSide);
      const end = anchor(cards[to], toSide);
      const path = pathForStream(from, end, fromSide, toSide, chain.bulge, Boolean(dropFrom));
      pushActor({ kind: 'stream', stack: stackIndex, chain, route, from: start, to, fromSide, toSide, drop: Boolean(dropFrom), path, started: now, duration: durationFor(path), trail: [], hops: 0 });
      return true;
    };
    const spawnBolt = (now: number) => {
      if (actors.filter(actor => actor.kind === 'bolt').length >= MAX_BOLTS || !activeSource.point || !chips.length) return false;
      const aim = boltCursor++ % chips.length;
      const target = chips[aim];
      const from = activeSource.point;
      const to = { x: target.x, y: target.y };
      const dy = to.y - from.y;
      const path: Curve = [from, { x: from.x + (to.x - from.x) * 0.15, y: from.y + dy * 0.65 }, { x: to.x - (to.x - from.x) * 0.15, y: to.y - dy * 0.52 }, to];
      const stack = stacks.length ? aim % stacks.length : 0;
      pushActor({ kind: 'bolt', path, started: now, duration: Math.max(580, durationFor(path)), trail: [], aim, stack, hue: target.hue });
      return true;
    };
    const spawnFlyer = (from: ZapPoint, to: ZapPoint, now: number) => {
      const onScreen = (p: ZapPoint) => p.x >= 0 && p.x <= width && p.y >= 0 && p.y <= height;
      if (!onScreen(from) || !onScreen(to)) return;
      const flyers = actors.filter(actor => actor.kind === 'flyer');
      if (flyers.length >= MAX_FLYERS) actors.splice(actors.indexOf(flyers[0]), 1);
      const dx = to.x - from.x;
      const path: Curve = [from, { x: from.x + dx * 0.35, y: from.y - 56 }, { x: to.x - dx * 0.25, y: to.y + 42 }, to];
      pushActor({ kind: 'flyer', path, started: now, duration: Math.max(500, durationFor(path)), trail: [], hue: '#8FE3F0' });
    };
    const eligibleStacks = () => stacks.map((cards, index) => ({ cards, index })).filter(entry => entry.cards.length >= 2);
    const canAnimate = () => !disposed && !reduced && !scrolling && !suspended && !document.hidden;
    const scheduleAmbient = () => {
      clearTimeout(ambientTimer);
      ambientTimer = undefined;
      if (!canAnimate() || !eligibleStacks().length) return;
      const now = performance.now();
      const untilStream = nextStreamAt > now ? nextStreamAt - now : 0;
      const untilBolt = activeSource.point && chips.length && nextBoltAt > now ? nextBoltAt - now : Infinity;
      const delay = Math.max(40, Math.min(untilStream, untilBolt));
      ambientTimer = setTimeout(() => {
        ambientTimer = undefined;
        if (!canAnimate()) return;
        const time = performance.now();
        if (time >= nextStreamAt) {
          const stacksAvailable = eligibleStacks();
          if (stacksAvailable.length) {
            const stack = stacksAvailable[stackCursor++ % stacksAvailable.length];
            spawnStream(stack.index, time);
          }
          nextStreamAt = time + (width < 640 ? 680 + Math.random() * 260 : 720 + Math.random() * 380);
        }
        if (time >= nextBoltAt) {
          spawnBolt(time);
          nextBoltAt = time + 5200 + Math.random() * 2400;
        }
        if (actors.length && !raf) raf = requestAnimationFrame(frame);
        scheduleAmbient();
      }, delay);
    };

    const drawTrail = (trail: ZapPoint[], hue: string, head: ZapPoint, boost = 1) => {
      if (trail.length < 2) return;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = rgba(hue, 0.95);
      ctx.shadowBlur = 18 * boost;
      for (let i = 1; i < trail.length; i++) {
        const strength = i / (trail.length - 1);
        ctx.globalAlpha = strength * strength * 0.9;
        ctx.strokeStyle = i === trail.length - 1 ? '#F8FDFF' : hue;
        ctx.lineWidth = (1.1 + strength * 2.3) * boost;
        ctx.beginPath();
        ctx.moveTo(trail[i - 1].x, trail[i - 1].y);
        ctx.lineTo(trail[i].x, trail[i].y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 22 * boost;
      ctx.fillStyle = '#F8FDFF';
      ctx.beginPath();
      ctx.arc(head.x, head.y, 3.6 * boost, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };
    const drawImpacts = (time: number) => {
      for (let i = rings.length - 1; i >= 0; i--) {
        const ring = rings[i];
        const progress = Math.max(0, Math.min(1, (time - ring.started) / ring.duration));
        if (progress >= 1) { rings.splice(i, 1); continue; }
        const alpha = (1 - progress) * 0.82;
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = ring.hue;
        ctx.lineWidth = 2.4 * (1 - progress) + 0.5;
        ctx.shadowColor = ring.hue;
        ctx.shadowBlur = 15 * (1 - progress);
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, 4 + ring.radius * progress, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      for (let i = bursts.length - 1; i >= 0; i--) {
        const burst = bursts[i];
        const progress = (time - burst.started) / burst.duration;
        if (progress >= 1) { bursts.splice(i, 1); continue; }
        const alpha = (1 - progress) * 0.95;
        const inner = 2 + progress * 8, outer = 7 + progress * 24;
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = burst.hue;
        ctx.shadowColor = burst.hue;
        ctx.shadowBlur = 18 * (1 - progress);
        ctx.lineWidth = 1.5 * (1 - progress) + 0.4;
        for (let ray = 0; ray < 8; ray++) {
          const angle = ray * Math.PI / 4 + 0.14;
          ctx.beginPath();
          ctx.moveTo(burst.x + Math.cos(angle) * inner, burst.y + Math.sin(angle) * inner);
          ctx.lineTo(burst.x + Math.cos(angle) * outer, burst.y + Math.sin(angle) * outer);
          ctx.stroke();
        }
        ctx.restore();
      }
    };
    const decayCharges = (delta: number) => {
      const decay = Math.exp(-delta / 980);
      for (const stack of stacks) for (const card of stack) {
        if (card.charge <= 0.015) continue;
        card.charge *= decay;
        if (Math.abs(card.charge - card.writtenCharge) >= 0.018 || card.charge <= 0.015) {
          if (card.charge <= 0.015) hideCharge(card);
          else {
            card.writtenCharge = card.charge;
            card.el.style.setProperty('--rd-charge', card.charge.toFixed(3));
          }
        }
      }
    };
    function frame(time: number) {
      raf = 0;
      if (!canAnimate()) return;
      if (lastPaint && time - lastPaint < 1000 / (width < 640 ? 24 : 30)) {
        raf = requestAnimationFrame(frame);
        return;
      }
      const delta = lastPaint ? Math.min(100, Math.max(1, time - lastPaint)) : 1000 / 30;
      painting = true;
      lastPaint = time;
      frames++;
      canvasElement.dataset.frames = String(frames);
      clearCanvas();
      for (let i = actors.length - 1; i >= 0; i--) {
        const actor = actors[i];
        const progress = Math.max(0, Math.min(1, (time - actor.started) / actor.duration));
        const head = cubic(actor.path, progress);
        actor.trail.push(head);
        if (actor.trail.length > MAX_TRAIL_POINTS) actor.trail.shift();
        const hue = actor.kind === 'stream' ? actor.chain.hue : actor.hue;
        drawTrail(actor.trail, hue, head, actor.kind === 'bolt' ? 1.15 : 1);
        if (progress < 1) continue;

        addImpact(head, hue, time, actor.kind === 'bolt' ? 1.15 : 0.85);
        if (actor.kind === 'stream') {
          lightCard(stacks[actor.stack]?.[actor.to]);
          const cards = stacks[actor.stack];
          const next = actor.to + actor.chain.step;
          if (cards && next < cards.length && actor.hops < 4) {
            actor.from = actor.to;
            actor.fromSide = actor.toSide;
            actor.to = next;
            actor.toSide = actor.route === 'zigzag' ? (actor.toSide === 1 ? -1 : 1) : actor.toSide;
            const from = anchor(cards[actor.from], actor.fromSide);
            const to = anchor(cards[actor.to], actor.toSide);
            actor.path = pathForStream(from, to, actor.fromSide, actor.toSide, actor.chain.bulge, false);
            actor.started = time;
            actor.duration = durationFor(actor.path);
            actor.trail = [];
            actor.hops++;
          } else actors.splice(i, 1);
        } else if (actor.kind === 'bolt') {
          const start = chips.length ? chips[actor.aim % chips.length] : null;
          if (start) spawnStream(actor.stack, time, { x: start.x, y: start.y });
          actors.splice(i, 1);
        } else {
          actors.splice(i, 1);
        }
      }
      updateActorCounts();
      drawImpacts(time);
      decayCharges(delta);
      const charged = stacks.some(stack => stack.some(card => card.charge > 0.015));
      painting = false;
      if (actors.length || rings.length || bursts.length || charged) raf = requestAnimationFrame(frame);
      else {
        lastPaint = 0;
        scheduleAmbient();
      }
    }

    const onScroll = () => {
      const firstScroll = !scrolling;
      scrolling = true;
      suspended = true;
      canvasElement.dataset.scrolling = 'true';
      if (firstScroll) {
        stopFrame();
        resetActors();
      }
      clearTimeout(ambientTimer);
      clearTimeout(settleTimer);
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        scrollTimer = undefined;
        if (disposed || document.hidden) return;
        if (resizePending) applySize();
        measure();
        scrolling = false;
        suspended = false;
        canvasElement.dataset.scrolling = 'false';
        nextStreamAt = performance.now() + 500;
        nextBoltAt = performance.now() + 2200;
        scheduleAmbient();
      }, SCROLL_SETTLE_MS);
    };
    const onVisibility = () => {
      stopFrame();
      clearTimeout(ambientTimer);
      ambientTimer = undefined;
      if (document.hidden) {
        suspended = true;
        scrolling = false;
        clearTimeout(scrollTimer);
        clearTimeout(settleTimer);
        scrollTimer = undefined;
        settleTimer = undefined;
        canvasElement.dataset.scrolling = 'false';
        resetActors();
        return;
      }
      clearTimeout(scrollTimer);
      scrollTimer = undefined;
      scrolling = false;
      canvasElement.dataset.scrolling = 'false';
      suspended = false;
      if (resizePending) applySize();
      measure();
      nextStreamAt = performance.now() + 650;
      nextBoltAt = performance.now() + 2200;
      scheduleAmbient();
    };
    const offZap = onZap((from, to) => {
      if (!canAnimate()) return;
      spawnFlyer(from, to, performance.now());
    });
    const offSection = onSectionChange(() => queueLayoutRefresh(180));
    const onResizeObserver = () => queueLayoutRefresh();
    if (main && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onResizeObserver);
      resizeObserver.observe(main);
      for (const column of document.querySelectorAll('[role="tabpanel"]:not([hidden]) [data-rd-cards]')) {
        resizeObserver.observe(column);
        watchedColumns.add(column);
      }
    }
    if (main && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(onResizeObserver);
      mutationObserver.observe(main, { childList: true, subtree: true });
    }

    if (resizePending || !width || !height) applySize();
    measure();
    nextStreamAt = performance.now() + 650;
    nextBoltAt = performance.now() + 1900;
    scheduleAmbient();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    document.fonts?.ready.then(() => { if (!disposed) queueLayoutRefresh(80); });

    return () => {
      disposed = true;
      resetTimers();
      stopFrame();
      resetActors();
      delete canvasElement.dataset.scrolling;
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      offZap();
      offSection();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reduced]);

  return <canvas ref={canvasRef} data-rd-zap-effects aria-hidden="true" className="fixed inset-0 w-screen h-screen pointer-events-none" style={{ zIndex: 30 }} />;
};
